// Date formatting utility for consistent DD-MM-YY format across the app
// All dates from backend (UTC) are automatically converted to user's local timezone

/**
 * Helper function to ensure UTC timestamps are parsed correctly
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {Date} Properly parsed Date object
 */
const parseAsUTC = (dateInput) => {
  if (!dateInput) return null;
  
  // If it's already a Date object, return it
  if (dateInput instanceof Date) return dateInput;
  
  // If it's a string without timezone info, append 'Z' to treat as UTC
  let dateString = dateInput;
  if (typeof dateInput === 'string' && !dateInput.endsWith('Z') && !dateInput.includes('+') && !dateInput.includes('-', 10)) {
    dateString = dateInput + 'Z';
  }
  
  return new Date(dateString);
};

/**
 * Format a date to DD-MM-YY format in user's local timezone
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string in DD-MM-YY format
 */
export const formatDateDDMMYY = (dateInput) => {
  const date = parseAsUTC(dateInput);
  if (!date || isNaN(date.getTime())) return '';
  
  // JavaScript Date automatically converts to local timezone when using getDate(), getMonth(), etc.
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  
  return `${day}-${month}-${year}`;
};

/**
 * Format a date for month grouping (e.g., "January 2024") in user's local timezone
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted month-year string
 */
export const formatMonthYear = (dateInput) => {
  const date = parseAsUTC(dateInput);
  if (!date || isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/**
 * Format a date range in DD-MM-YY format
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted date range
 */
export const formatDateRange = (startDate, endDate) => {
  return `${formatDateDDMMYY(startDate)} - ${formatDateDDMMYY(endDate)}`;
};

/**
 * Format time in user's local timezone (e.g., "3:45 PM")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted time string
 */
export const formatTime = (dateInput) => {
  const date = parseAsUTC(dateInput);
  if (!date || isNaN(date.getTime())) return '';
  
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format date and time in user's local timezone (e.g., "01-01-26 3:45 PM")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date-time string
 */
export const formatDateTime = (dateInput) => {
  if (!dateInput) return '';
  return `${formatDateDDMMYY(dateInput)} ${formatTime(dateInput)}`;
};

/**
 * Format relative time (e.g., "Just now", "5m ago", "2h ago")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateInput) => {
  const date = parseAsUTC(dateInput);
  if (!date || isNaN(date.getTime())) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  
  // For dates older than 7 days, show full date in user's local timezone
  return formatDateDDMMYY(dateInput);
};

/**
 * Get user's timezone abbreviation (e.g., "IST", "EST")
 * @returns {string} Timezone abbreviation
 */
export const getTimezoneAbbr = () => {
  const date = new Date();
  const timeString = date.toLocaleTimeString('en-US', { timeZoneName: 'short' });
  return timeString.split(' ').pop();
};

/**
 * Get user's full timezone name (e.g., "Asia/Kolkata")
 * @returns {string} Timezone name
 */
export const getTimezoneName = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format date for input[type="date"] value (YYYY-MM-DD in local timezone)
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatForDateInput = (dateInput) => {
  const date = parseAsUTC(dateInput);
  if (!date || isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Parse date from input[type="date"] to local Date object
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} Local Date object
 */
export const parseDateInput = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0); // Noon to avoid timezone edge cases
};

/**
 * Convert local date to UTC ISO string for backend
 * @param {Date|string} date - Local date
 * @returns {string} UTC ISO string
 */
export const toUtcString = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default formatDateDDMMYY;
